/**
 * プレイエリア（器）の形状計算。Phaser 非依存の純粋ロジック。
 *
 * 器の内側は「下半分のスーパー楕円」で表し、口の左右には外へ開く縁（リップ）を付ける。
 * `flatness` が 2 のときはただの半楕円で、上げるほど底が平らになる。
 * 料理はこの中に積まれ、口からあふれる／縁を越えて外へ落ちるとゲームオーバーになる。
 *
 * 座標系は GameScene の論理解像度（480x720）と同じ。
 */

export interface BowlConfig {
  centerX: number;
  rimY: number;
  rimHalf: number;
  depth: number;
  segments: number;
  lipOut: number;
  lipUp: number;
  /**
   * 底の平らさ（スーパー楕円の指数）。省略すると 2＝ただの半楕円。
   *
   * |x/a|^n + |y/b|^n = 1 の n。上げるほど底が平らになり、
   * 底のほうでも大きい料理が2つ並べるようになる。
   */
  flatness?: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface BowlGeometry {
  readonly centerX: number;
  /** 口（縁）の Y。あふれ判定のライン。 */
  readonly rimY: number;
  readonly rimHalf: number;
  readonly depth: number;
  /** 底の内側の Y。 */
  readonly bottomY: number;
  /** 縁の先端（リップ）の座標。 */
  readonly lipLeft: Point;
  readonly lipRight: Point;
  /**
   * 内壁の輪郭（左のリップ → 左の口 → 底 → 右の口 → 右のリップ）。
   * 物理の壁と見た目の両方で使う。
   */
  readonly contour: readonly Point[];
  /** 指定 Y における器の内側の半幅。 */
  halfWidthAt(y: number): number;
  /** 半径 radius の料理が沈める最も深い中心 Y。 */
  deepestY(radius: number): number;
  /** 半径 radius の料理の中心が動ける X の範囲（口の高さ基準）。 */
  dropRange(radius: number): { min: number; max: number };
}

/** 器の内側の輪郭と判定用の計算をまとめて作る。 */
export function createBowlGeometry(config: BowlConfig): BowlGeometry {
  const { centerX, rimY, rimHalf, depth, lipOut, lipUp } = config;
  const segments = Math.max(4, Math.floor(config.segments));
  const flatness = Math.max(2, config.flatness ?? 2);
  // スーパー楕円の媒介変数表示で使う指数。flatness=2 なら 1 になり、従来の楕円と完全に一致する
  const power = 2 / flatness;

  const lipLeft: Point = { x: centerX - rimHalf - lipOut, y: rimY - lipUp };
  const lipRight: Point = { x: centerX + rimHalf + lipOut, y: rimY - lipUp };

  // 下半分のスーパー楕円: t=0 で左の口、t=0.5 で底の中央、t=1 で右の口
  const arc: Point[] = [];
  for (let i = 0; i <= segments; i += 1) {
    const angle = Math.PI * (i / segments);
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    arc.push({
      x: centerX - rimHalf * Math.sign(cos) * Math.abs(cos) ** power,
      y: rimY + depth * Math.abs(sin) ** power,
    });
  }

  const contour: Point[] = [lipLeft, ...arc, lipRight];

  function halfWidthAt(y: number): number {
    if (y <= rimY) return rimHalf;
    const ratio = (y - rimY) / depth;
    if (ratio >= 1) return 0;
    return rimHalf * Math.max(0, 1 - ratio ** flatness) ** (1 / flatness);
  }

  function deepestY(radius: number): number {
    if (radius >= rimHalf) return rimY;
    const ratio = Math.max(0, 1 - (radius / rimHalf) ** flatness) ** (1 / flatness);
    return rimY + depth * ratio;
  }

  function dropRange(radius: number): { min: number; max: number } {
    const room = Math.max(0, rimHalf - radius - 2);
    return { min: centerX - room, max: centerX + room };
  }

  return {
    centerX,
    rimY,
    rimHalf,
    depth,
    bottomY: rimY + depth,
    lipLeft,
    lipRight,
    contour,
    halfWidthAt,
    deepestY,
    dropRange,
  };
}

/**
 * 輪郭を外側（器の中心から遠ざかる向き）へ offset だけ押し出した多角形を返す。
 * 陶器の厚みを描くために使う。
 */
export function offsetContour(bowl: BowlGeometry, offset: number): Point[] {
  const focus = { x: bowl.centerX, y: bowl.rimY + bowl.depth * 0.5 };
  return bowl.contour.map((point) => {
    const dx = point.x - focus.x;
    const dy = point.y - focus.y;
    const length = Math.hypot(dx, dy) || 1;
    return { x: point.x + (dx / length) * offset, y: point.y + (dy / length) * offset };
  });
}
